import type { AxisScale } from '@visx/xychart';

import { ZOOM_SNAP_DISTANCE_PX } from '../../../../constants';
import { LineChartSnapStrategy } from '../../tooltipSnap/strategies';
import type { SnapStrategyContext } from '../../tooltipSnap/types';
import type { DomainCalculator, DomainCalculatorContext } from '../types';

/**
 * Domain calculator for continuous scales (used in line charts with time/linear scales).
 * Handles snapping to data points and ensures domain matches data point boundaries.
 */
export class ContinuousScaleDomainCalculator implements DomainCalculator {
  private readonly snapStrategy = new LineChartSnapStrategy();

  calculateDomain(
    selectionStartX: number,
    selectionEndX: number,
    xScale: AxisScale,
    context: DomainCalculatorContext,
  ): [number, number] | string[] | null {
    const continuousScale = xScale as { invert: (value: number) => number | Date };

    // Check if invert method exists
    if (typeof continuousScale.invert !== 'function') {
      return null;
    }

    const startPixel = Math.min(selectionStartX, selectionEndX);
    const endPixel = Math.max(selectionStartX, selectionEndX);

    // Try to snap to nearby data points for easier selection of first/last data points
    let snappedStart: number | null = null;
    let snappedEnd: number | null = null;

    // Build snap strategy context for start pixel
    const startSnapContext: SnapStrategyContext = {
      cursorSvgX: startPixel,
      chartData: context.chartData,
      xDataKey: context.xDataKey,
      xScale: xScale as (value: number) => number | undefined,
      snapDistance: ZOOM_SNAP_DISTANCE_PX,
      xScaleType: context.xScaleType,
      zoomDomain: context.zoomDomain,
    };

    // Check if start pixel is near a data point
    const closestStartPoint = this.snapStrategy.findClosestDataPoint(startSnapContext);
    if (closestStartPoint && typeof closestStartPoint.xValue === 'number') {
      // For continuous scales (line charts), xValue is always a number (timestamp/numeric value)
      snappedStart = closestStartPoint.xValue;
    }

    // Build snap strategy context for end pixel
    const endSnapContext: SnapStrategyContext = {
      cursorSvgX: endPixel,
      chartData: context.chartData,
      xDataKey: context.xDataKey,
      xScale: xScale as (value: number) => number | undefined,
      snapDistance: ZOOM_SNAP_DISTANCE_PX,
      xScaleType: context.xScaleType,
      zoomDomain: context.zoomDomain,
    };

    // Check if end pixel is near a data point
    const closestEndPoint = this.snapStrategy.findClosestDataPoint(endSnapContext);
    if (closestEndPoint && typeof closestEndPoint.xValue === 'number') {
      // For continuous scales (line charts), xValue is always a number (timestamp/numeric value)
      snappedEnd = closestEndPoint.xValue;
    }

    // Get approximate domain from pixel positions (use snapped values if available)
    const approximateStart = snappedStart !== null ? snappedStart : continuousScale.invert(startPixel);
    const approximateEnd = snappedEnd !== null ? snappedEnd : continuousScale.invert(endPixel);

    // Validate invert results
    if (
      approximateStart === null ||
      approximateStart === undefined ||
      approximateEnd === null ||
      approximateEnd === undefined ||
      Number.isNaN(approximateStart) ||
      Number.isNaN(approximateEnd)
    ) {
      return null;
    }

    // Get numeric values (for time scales, convert Date to timestamp)
    const approxStart = approximateStart instanceof Date ? approximateStart.getTime() : approximateStart;
    const approxEnd = approximateEnd instanceof Date ? approximateEnd.getTime() : approximateEnd;

    // Validate numeric values
    if (
      typeof approxStart !== 'number' ||
      typeof approxEnd !== 'number' ||
      Number.isNaN(approxStart) ||
      Number.isNaN(approxEnd) ||
      approxStart === approxEnd
    ) {
      return null;
    }

    // Snap to actual data points within the selection to prevent lines from extending beyond
    // Get all x values from chartData, but only include those within current zoom domain (if zoomed)
    const xValues = new Set<number>();

    // Determine the effective domain to filter data points
    // If there's a current zoom domain, only consider points within it
    let effectiveMin: number | null = null;
    let effectiveMax: number | null = null;
    if (context.zoomDomain && Array.isArray(context.zoomDomain) && context.zoomDomain.length === 2) {
      const [zoomMin, zoomMax] = context.zoomDomain;
      if (typeof zoomMin === 'number' && typeof zoomMax === 'number') {
        effectiveMin = Math.min(zoomMin, zoomMax);
        effectiveMax = Math.max(zoomMin, zoomMax);
      }
    }

    for (const datum of context.chartData) {
      const xValue = datum[context.xDataKey];
      if (typeof xValue === 'number') {
        // Only include xValue if it's within the current zoom domain (if zoomed)
        if (effectiveMin === null || effectiveMax === null || (xValue >= effectiveMin && xValue <= effectiveMax)) {
          xValues.add(xValue);
        }
      }
    }

    // Find min and max data points within the approximate selection range
    // This ensures the domain exactly matches data point boundaries
    const sortedXValues = Array.from(xValues).sort((a, b) => a - b);
    const [approxMin, approxMax] = approxStart < approxEnd ? [approxStart, approxEnd] : [approxEnd, approxStart];

    // Find the first data point >= approxMin and last data point <= approxMax
    // Use binary search for better performance, but simple iteration is fine for small datasets
    let minDataPoint: number | null = null;
    let maxDataPoint: number | null = null;

    for (const xValue of sortedXValues) {
      // Find first data point >= approxMin
      if (xValue >= approxMin && minDataPoint === null) {
        minDataPoint = xValue;
      }
      // Find last data point <= approxMax (keep updating until we find the last one)
      if (xValue <= approxMax) {
        maxDataPoint = xValue;
      } else {
        // Once we exceed approxMax, we can break since values are sorted
        break;
      }
    }

    // Only zoom if we found at least 2 distinct data points (area charts need 2+ points to render)
    if (minDataPoint !== null && maxDataPoint !== null && minDataPoint < maxDataPoint) {
      return [minDataPoint, maxDataPoint];
    }

    // Don't zoom if no data points found in range
    return null;
  }
}
